import { useState } from "preact/hooks"
import { format, parseISO } from "date-fns"
import { Check, Circle, ListChecks, Pencil, Plus, Sparkles, User2 } from "lucide-react"
import { DeductionDetailJson } from "@/api/deduction.tsx"
import {
  ChecklistResponse,
  ChecklistStepResponse,
  complete_checklist_step,
  get_deduction_checklist,
  uncomplete_checklist_step,
} from "@/api/checklist.tsx"
import { get_display_name, useFetch } from "@/utils"
import { RefreshFlag } from "@/refresh_flag.tsx"
import { CreateChecklistDialog } from "./CreateChecklistDialog.tsx"

interface ChecklistSectionProps {
  deduction: DeductionDetailJson
  className?: string
}

const TERMINAL_STATUSES = ["won", "lost", "validated", "archived"]

export function ChecklistSection({ deduction, className }: ChecklistSectionProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const refreshFlag = RefreshFlag.use((f: boolean) => f)
  const isTerminal = TERMINAL_STATUSES.includes(deduction.status_value)

  const { data: checklist, refetch } = useFetch(
    () => get_deduction_checklist(deduction.id),
    [deduction.id, refreshFlag]
  )

  // For terminal statuses (won, lost, etc.), only show if there's progress on the checklist
  if (isTerminal) {
    const hasProgress = checklist?.steps.some((s: ChecklistStepResponse) => s.is_completed) ?? false
    if (!hasProgress) return null
  }

  if (!checklist && !showCreateDialog) {
    return (
      <div class={`${className || ""}`}>
        <EmptyChecklistPrompt onCreateClick={() => setShowCreateDialog(true)} />
        {showCreateDialog && (
          <CreateChecklistDialog
            deduction={deduction}
            onClose={() => setShowCreateDialog(false)}
            onCreated={() => {
              setShowCreateDialog(false)
              refetch()
            }}
          />
        )}
      </div>
    )
  }

  return (
    <div class={`${className || ""}`}>
      {checklist ? (
        <ChecklistCard
          checklist={checklist}
          deduction={deduction}
          onRefetch={refetch}
          onEdit={() => setEditMode(true)}
        />
      ) : null}

      {showCreateDialog && (
        <CreateChecklistDialog
          deduction={deduction}
          onClose={() => setShowCreateDialog(false)}
          onCreated={() => {
            setShowCreateDialog(false)
            refetch()
          }}
        />
      )}

      {editMode && checklist && (
        <CreateChecklistDialog
          deduction={deduction}
          existingChecklist={checklist}
          onClose={() => setEditMode(false)}
          onCreated={() => {
            setEditMode(false)
            refetch()
          }}
        />
      )}
    </div>
  )
}

interface EmptyChecklistPromptProps {
  onCreateClick: () => void
}

function EmptyChecklistPrompt({ onCreateClick }: EmptyChecklistPromptProps) {
  return (
    <button
      onClick={onCreateClick}
      class="w-full border border-dashed border-slate-300 rounded-lg p-4 flex items-center gap-3 text-slate-500 hover:border-slate-400 hover:text-slate-600 transition-colors cursor-pointer">
      <ListChecks className="h-5 w-5 shrink-0" />
      <div class="text-left">
        <div class="text-sm font-medium">Set up validation checklist</div>
        <div class="text-xs text-slate-400">Track steps to validate this deduction</div>
      </div>
      <Plus className="h-4 w-4 ml-auto shrink-0" />
    </button>
  )
}

interface ChecklistCardProps {
  checklist: ChecklistResponse
  deduction: DeductionDetailJson
  onRefetch: () => void
  onEdit: () => void
}

function ChecklistCard({ checklist, deduction, onRefetch, onEdit }: ChecklistCardProps) {
  const completedCount = checklist.steps.filter((s: ChecklistStepResponse) => s.is_completed).length
  const totalCount = checklist.steps.length
  const allComplete = completedCount === totalCount && totalCount > 0

  return (
    <div class="border border-slate-200 rounded-lg overflow-hidden">
      <div class="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
        <div class="flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-slate-600" />
          <span class="text-sm font-semibold text-slate-700">{checklist.checklist.name}</span>
        </div>
        <div class="flex items-center gap-2">
          <span class={`text-xs font-medium ${allComplete ? "text-green-600" : "text-slate-500"}`}>
            {completedCount}/{totalCount}
          </span>
          <button
            onClick={onEdit}
            class="text-slate-400 hover:text-slate-600 p-0.5 rounded cursor-pointer"
            title="Edit checklist">
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div class="divide-y divide-slate-100">
        {checklist.steps.map((step: ChecklistStepResponse) => (
          <ChecklistStepRow key={step.id} step={step} deduction_id={deduction.id} onRefetch={onRefetch} />
        ))}
      </div>
      {allComplete && (
        <div class="px-4 py-2 bg-green-50 border-t border-green-100 text-green-700 text-xs font-medium flex items-center gap-1.5">
          <Check className="h-3.5 w-3.5" />
          All steps complete
        </div>
      )}
    </div>
  )
}

interface ChecklistStepRowProps {
  step: ChecklistStepResponse
  deduction_id: string
  onRefetch: () => void
}

function ChecklistStepRow({ step, deduction_id, onRefetch }: ChecklistStepRowProps) {
  const [loading, setLoading] = useState(false)

  async function handleToggle() {
    if (loading) return
    // Don't allow uncompleting auto-completed steps
    if (step.auto_completed) return

    setLoading(true)
    try {
      if (step.is_completed && !step.auto_completed) {
        await uncomplete_checklist_step(step.id, deduction_id)
      } else if (!step.is_completed) {
        await complete_checklist_step(step.id, deduction_id)
      }
      onRefetch()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      class={`flex items-start gap-3 px-4 py-2.5 group ${step.is_completed ? "bg-slate-50/50" : "hover:bg-slate-50"}`}>
      <button
        onClick={handleToggle}
        disabled={loading || step.auto_completed}
        class={`mt-0.5 shrink-0 cursor-pointer ${step.auto_completed ? "cursor-default" : ""}`}>
        {step.is_completed ? (
          <div
            class={`h-4.5 w-4.5 rounded-full flex items-center justify-center ${
              step.auto_completed ? "bg-blue-100 text-blue-600" : "bg-green-100 text-green-600"
            }`}>
            {step.auto_completed ? <Sparkles className="h-3 w-3" /> : <Check className="h-3 w-3" />}
          </div>
        ) : (
          <Circle className="h-4.5 w-4.5 text-slate-300 group-hover:text-slate-400" />
        )}
      </button>

      <div class="flex-1 min-w-0">
        <div class={`text-sm leading-tight ${step.is_completed ? "text-slate-500" : "text-slate-700"}`}>
          {step.title}
        </div>
        <StepMetadata step={step} />
      </div>
    </div>
  )
}

interface StepMetadataProps {
  step: ChecklistStepResponse
}

function StepMetadata({ step }: StepMetadataProps) {
  // Show auto-completed badge
  if (step.auto_completed) {
    return (
      <div class="flex items-center gap-1 mt-0.5">
        <span class="text-xs text-blue-500 font-medium">Auto-completed</span>
      </div>
    )
  }

  // Show manual completion info
  if (step.is_completed && step.completed_at) {
    return (
      <div class="flex items-center gap-1.5 mt-0.5 text-xs text-slate-400">
        {step.completed_by_name && (
          <>
            <User2 className="h-3 w-3" />
            <span>{step.completed_by_name}</span>
            <span>&middot;</span>
          </>
        )}
        <span>{format(parseISO(step.completed_at), "MMM d")}</span>
      </div>
    )
  }

  // Show pending task info
  if (step.pending_task) {
    return (
      <div class="flex items-center gap-1.5 mt-0.5 text-xs text-amber-600">
        <span class="inline-block h-1.5 w-1.5 rounded-full bg-amber-400" />
        <span>
          Task assigned{step.pending_task.assigned_to ? ` to ${get_display_name(step.pending_task.assigned_to)}` : ""}
        </span>
      </div>
    )
  }

  return null
}
